import { useEffect, useRef } from 'react';
import { isRealValue } from '@/utils/helpers';

/**
 * Hook to debounce search text and sync it to URL search params,
 * resetting pagination to the first page on each new search.
 *
 * @param {Object} options
 * @param {string} options.searchText - Current search text value
 * @param {Function} options.setSearchParams - React Router's setSearchParams
 * @param {Function} options.onPaginationChange - Pagination change handler from usePagination
 * @param {number} options.limit - Current page size
 * @param {number} [options.delay=200] - Debounce delay in ms
 */
export const useDebouncedSearchSync = ({ searchText, setSearchParams, onPaginationChange, limit, delay = 200 }) => {
  const isInitialRender = useRef(true);

  useEffect(() => {
    // Skip the initial render to avoid syncing default state to URL
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    const timeoutId = setTimeout(() => {
      setSearchParams((params) => {
        if (isRealValue(searchText) && searchText !== '') {
          params.set('search', searchText);
        } else {
          params.delete('search');
        }
        onPaginationChange({ pageSize: limit, pageIndex: 0 });
        return params;
      });
    }, delay);
    return () => clearTimeout(timeoutId);
  }, [searchText]);
};
